// Swipe bookkeeping for the Cari deck.
//
// The server is the record of truth (`POST /discover/swipe`), but the
// decision has to survive a reload and a dead network too: a destination the
// traveller dismissed must never reappear, and the undo has to work even if
// the request that recorded the swipe is still in flight. So every swipe is
// mirrored here, keyed by placeId, and the deck filters against it.
import type { DiscoverCard, SwipeDirection } from "./types";

const KEY = "traveldiary.swipes";
const KEY_LAST = "traveldiary.lastSwipe";

export type SwipeLog = Record<string, SwipeDirection>;

export function readSwipes(): SwipeLog {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: SwipeLog = {};
    for (const [placeId, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (value === "left" || value === "right") out[placeId] = value;
    }
    return out;
  } catch {
    return {};
  }
}

export function writeSwipes(log: SwipeLog): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(log));
  } catch {
    // Storage disabled. The deck still behaves for this session.
  }
}

export function recordSwipeLocal(log: SwipeLog, placeId: string, direction: SwipeDirection): SwipeLog {
  const next = { ...log, [placeId]: direction };
  writeSwipes(next);
  return next;
}

export function clearSwipeLocal(log: SwipeLog, placeId: string): SwipeLog {
  const next = { ...log };
  delete next[placeId];
  writeSwipes(next);
  return next;
}

/** A card with no photo is not a card, per the contract. */
export function isShowable(card: DiscoverCard): boolean {
  return card.media.length > 0;
}

export function filterDeck(cards: DiscoverCard[], log: SwipeLog): DiscoverCard[] {
  return cards.filter((card) => isShowable(card) && !log[card.placeId]);
}

/**
 * The one swipe that can still be undone, kept in storage rather than in
 * component state. A traveller who accepts a destination, taps through to
 * their wishlist to look at it, then comes back would otherwise find the
 * undo greyed out: the mistake is still one tap old, but the state that
 * knew about it died with the unmount.
 */
